<section class="delivery-country">
  <header>
    <h3><i class="fa-solid fa-earth-europe"></i> {{ 'DELIVERY_COUNTRY_TITLE' | translate }}</h3>
    <p>{{ 'DELIVERY_COUNTRY_HELP' | translate }}</p>
  </header>

  @if (loading) {
    <div class="text-center py-2"><span class="spinner-border spinner-border-sm"></span></div>
  }
  @if (error) {
    <div class="alert alert-danger py-2">{{ error | translate }}</div>
  }

  @if (source) {
    <div class="delivery-country__facts">
      <div>
        <span>{{ 'AMAZON_ORDER' | translate }}</span>
        <a
          [href]="'https://sellercentral.amazon.de/orders-v3/order/' + source.amazonOrderId"
          target="_blank"
          rel="noopener"
          >{{ source.amazonOrderId }} <i class="fa-solid fa-arrow-up-right-from-square"></i
        ></a>
      </div>
      <div>
        <span>{{ 'DELIVERY_COUNTRY_CURRENT' | translate }}</span>
        <strong>{{ source.deliveryCountry || '—' }}</strong>
      </div>
      <div>
        <span>{{ 'MARKETPLACE' | translate }}</span>
        <strong>{{ source.marketplace || '—' }} · {{ source.currency || '—' }}</strong>
      </div>
      <div>
        <span>{{ 'DELIVERY_COUNTRY_VAT' | translate }}</span>
        <strong>
          @if (source.vatImplied !== null && source.vatImplied !== undefined) {
            {{ source.vatImplied | number: '1.0-2' }} %
          } @else {
            —
          }
        </strong>
      </div>
    </div>

    @if (source.marketplaceFacilitatorVat) {
      <p class="small mb-2">{{ 'DELIVERY_COUNTRY_FACILITATOR_VAT' | translate }}</p>
    }

    @if (source.lookupFailure) {
      <div class="alert py-2" [ngClass]="source.lookupRetryable ? 'alert-warning' : 'alert-danger'">
        <strong>{{ 'DELIVERY_COUNTRY_LOOKUP_FAILED' | translate }}</strong>
        <div class="small text-break">{{ source.lookupFailure }}</div>
        <div class="small mt-1">
          {{ (source.lookupRetryable ? 'DELIVERY_COUNTRY_LOOKUP_RETRY' : 'DELIVERY_COUNTRY_LOOKUP_SET_BY_HAND') | translate }}
        </div>
      </div>
    }

    @if (source.override) {
      <div class="alert alert-info py-2 delivery-country__override">
        <div>
          {{ 'DELIVERY_COUNTRY_SET_BY_HAND' | translate }}:
          <strong>{{ source.override.erpCountry }} ({{ source.override.countryCode }})</strong>,
          {{ source.override.setBy }} {{ source.override.setAt | date: 'yyyy-MM-dd HH:mm' }}
          @if (source.override.reason) {
            <div class="small">{{ source.override.reason }}</div>
          }
        </div>
        <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="working" (click)="clear()">
          {{ 'DELIVERY_COUNTRY_CLEAR' | translate }}
        </button>
      </div>
    }

    <h4>
      @if (source.apiOrder) {
        {{ 'DELIVERY_COUNTRY_API' | translate }}
        @if (source.apiOrderFetchedAt) {
          <small>{{ source.apiOrderFetchedAt | date: 'yyyy-MM-dd HH:mm' }}</small>
        }
      } @else if (source.reportOrder) {
        {{ 'DELIVERY_COUNTRY_REPORT' | translate }}
      } @else {
        {{ 'DELIVERY_COUNTRY_NO_ORDER_DATA' | translate }}
      }
    </h4>
    @if (source.apiOrder || source.reportOrder) {
      <table class="table table-sm delivery-country__facts-table">
        <tbody>
          @for (fact of orderFacts(); track fact.label) {
            <tr [class.table-warning]="fact.highlight">
              <th>{{ fact.label | translate }}</th>
              <td>{{ fact.value }}</td>
            </tr>
          }
        </tbody>
      </table>
    }
    <button type="button" class="btn btn-sm btn-outline-primary mb-3" [disabled]="working" (click)="refetch()">
      <span *ngIf="working" class="spinner-border spinner-border-sm"></span>
      <i *ngIf="!working" class="fa-solid fa-cloud-arrow-down"></i>
      {{ 'DELIVERY_COUNTRY_REFETCH' | translate }}
    </button>
    @if (source.reportOrder) {
      <details>
        <summary>{{ 'DELIVERY_COUNTRY_REPORT_RAW' | translate }}</summary>
        <pre>{{ source.reportOrder | json }}</pre>
      </details>
    }
    @if (source.apiOrder) {
      <details>
        <summary>{{ 'DELIVERY_COUNTRY_API_RAW' | translate }}</summary>
        <pre>{{ source.apiOrder | json }}</pre>
      </details>
    }

    @if (source.suggestions?.length) {
      <div class="delivery-country__suggestions">
        <span>{{ 'DELIVERY_COUNTRY_SUGGESTIONS' | translate }}</span>
        @for (suggestion of source.suggestions; track suggestion.countryCode) {
          <button
            type="button"
            class="btn btn-sm"
            [ngClass]="countryCode === suggestion.countryCode ? 'btn-primary' : 'btn-outline-primary'"
            (click)="choose(suggestion.countryCode)"
          >
            {{ countryName(suggestion.countryCode) }} ({{ suggestion.countryCode }})
            <small>
              @for (reason of suggestion.reasons; track reason; let last = $last) {
                {{ 'DELIVERY_COUNTRY_REASON_' + reason | translate }}{{ last ? '' : ', ' }}
              }
            </small>
          </button>
        }
      </div>
    }

    <form class="delivery-country__form" (ngSubmit)="save()">
      <label>
        <span>{{ 'DELIVERY_COUNTRY_SET' | translate }}</span>
        <select class="form-select form-select-sm" name="countryCode" [(ngModel)]="countryCode" required>
          <option value="">{{ 'DELIVERY_COUNTRY_CHOOSE' | translate }}</option>
          @for (country of countries; track country.code) {
            <option [value]="country.code">
              {{ country.name }} ({{ country.code }})@if (country.vat !== undefined) {
                · {{ country.vat | number: '1.0-2' }} %
              }@if (country.matchesVat) {
                ✓
              }
            </option>
          }
        </select>
        @if (matchingCountries()) {
          <small class="d-block mt-1">
            {{ 'DELIVERY_COUNTRY_VAT_MATCHES' | translate: { vat: (source.vatImplied | number: '1.0-2') } }}
            {{ matchingCountries() }}
          </small>
        }
      </label>
      <label>
        <span>{{ 'DELIVERY_COUNTRY_REASON' | translate }}</span>
        <input
          class="form-control form-control-sm"
          name="reason"
          maxlength="500"
          [(ngModel)]="reason"
          [placeholder]="'DELIVERY_COUNTRY_REASON_PLACEHOLDER' | translate"
        />
      </label>
      <button type="submit" class="btn btn-sm btn-primary" [disabled]="working || !countryCode">
        {{ 'DELIVERY_COUNTRY_SAVE' | translate }}
      </button>
    </form>

    @if (source.remapResult) {
      <div class="alert py-2" [ngClass]="source.remapResult.rejected.length ? 'alert-warning' : 'alert-success'">
        {{ 'DELIVERY_COUNTRY_REMAPPED' | translate: { count: source.remapResult.updated.length } }}
        @for (entry of remapCounts(); track entry.status) {
          <code>{{ entry.status }}: {{ entry.count }}</code>
        }
        @for (failure of source.remapResult.rejected; track failure.transactionId) {
          <div class="small">{{ failure.transactionId }}: {{ failure.reason }}</div>
        }
      </div>
    }

    @if (source.orderTransactions.length > 1) {
      <details>
        <summary>{{ 'DELIVERY_COUNTRY_ORDER_TRANSACTIONS' | translate: { count: source.orderTransactions.length } }}</summary>
        <ul class="delivery-country__transactions">
          @for (transaction of source.orderTransactions; track transaction.id) {
            <li>
              <code>{{ transaction.id }}</code> {{ transaction.postedDate | date: 'yyyy-MM-dd' }}
              <strong>{{ transaction.mappingStatus }}</strong>
            </li>
          }
        </ul>
      </details>
    }
  }
</section>
